import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from '../lib/api';

interface QiProfileAdminDto {
  id: number;
  lastName?: string;
  firstName?: string;
  middleInitial?: string;
  examDate?: string;
  providerName?: string;
  providerEmail?: string;
}

interface AetbProfileAdminDto {
  id: number;
  name?: string;
  phone?: string;
  email?: string;
}

interface ProfileListsDto {
  qi: QiProfileAdminDto[];
  aetb: AetbProfileAdminDto[];
}

/** Admin-only management of the saved QI / AETB profiles the conversion screens offer. */
export function ProfilesAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => apiClient.get<ProfileListsDto>('/api/profiles'),
  });
  const [qi, setQi] = useState<Record<string, string>>({});
  const [aetb, setAetb] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await qc.invalidateQueries({ queryKey: ['profiles'] });
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403
        ? 'Only Admins can change profiles.'
        : 'That change failed — try again.');
    }
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <h2 className="text-xl font-semibold text-atg-navy">QI / AETB profiles</h2>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <section className="space-y-3">
        <h3 className="font-semibold">Qualified individuals</h3>
        <table className="text-sm w-full border-collapse bg-white border border-slate-200 rounded-md">
          <tbody>
            {(data?.qi ?? []).map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="p-2">{p.firstName} {p.middleInitial ? `${p.middleInitial}. ` : ''}{p.lastName}</td>
                <td className="p-2 text-slate-500">exam {p.examDate ?? '—'}</td>
                <td className="p-2 text-slate-500">{p.providerName}</td>
                <td className="p-2 text-right">
                  <button className="text-red-700 hover:underline"
                    onClick={() => void run(() => apiClient.del(`/api/profiles/qi/${p.id}`))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-2 gap-2">
          {[['lastName', 'Last name'], ['firstName', 'First name'], ['middleInitial', 'Middle initial (optional)'],
            ['examDate', 'Exam date (2024-02-15)'], ['providerName', 'Provider name'], ['providerEmail', 'Provider email']]
            .map(([key, label]) => (
              <input key={key} className="border border-slate-300 rounded px-2 py-1 text-sm"
                placeholder={label} value={qi[key] ?? ''}
                onChange={(e) => setQi((prev) => ({ ...prev, [key]: e.target.value }))} />
            ))}
        </div>
        <button className="px-3 py-1.5 rounded bg-atg-navy text-white text-sm"
          onClick={() => void run(() => apiClient.post('/api/profiles/qi', qi).then(() => setQi({})))}>
          Add QI profile
        </button>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">Testing bodies (AETB)</h3>
        <table className="text-sm w-full border-collapse bg-white border border-slate-200 rounded-md">
          <tbody>
            {(data?.aetb ?? []).map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="p-2">{p.name}</td>
                <td className="p-2 text-slate-500">{p.phone}</td>
                <td className="p-2 text-slate-500">{p.email}</td>
                <td className="p-2 text-right">
                  <button className="text-red-700 hover:underline"
                    onClick={() => void run(() => apiClient.del(`/api/profiles/aetb/${p.id}`))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-3 gap-2">
          {[['name', 'Company name'], ['phone', 'Phone'], ['email', 'Email']].map(([key, label]) => (
            <input key={key} className="border border-slate-300 rounded px-2 py-1 text-sm"
              placeholder={label} value={aetb[key] ?? ''}
              onChange={(e) => setAetb((prev) => ({ ...prev, [key]: e.target.value }))} />
          ))}
        </div>
        <button className="px-3 py-1.5 rounded bg-atg-navy text-white text-sm"
          onClick={() => void run(() => apiClient.post('/api/profiles/aetb', aetb).then(() => setAetb({})))}>
          Add AETB profile
        </button>
      </section>
    </div>
  );
}
